// useGetSuccessorsDocs.ts
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import axiosInstance from "axiosConfig";
import { SuccessorDocs } from "types/SuccessorTypes";
import { queryClient } from "App";
import { SESSION_QUERY_KEY } from "./CheckSessionHook";

interface GetSuccessorsDocsHookType {
  enabled?: boolean;
}

interface OwnerSuccessorDocs {
  ownerName: string;
  documents: SuccessorDocs[];
}

type BackendResp = {
  status: string;
  data: SuccessorDocs[];
  canViewFiles: boolean | number;
  successorsData?: OwnerSuccessorDocs[];
};

type Normalized = {
  docs: SuccessorDocs[];
  canViewFiles: boolean;
  successorsData: OwnerSuccessorDocs[];
};

export function useGetSuccessorsDocs({ enabled }: GetSuccessorsDocsHookType) {
  const query = useQuery<BackendResp, unknown, Normalized>({
    queryKey: ["successors", "docs"],
    queryFn: async (): Promise<BackendResp> => {
      const res = await axiosInstance.get(`/successors/docs`);
      return res.data;
    },
    placeholderData: keepPreviousData,
    select: (payload) => ({
      docs: payload.data ?? [],
      canViewFiles: !!payload.canViewFiles,
      successorsData: payload.successorsData ?? [],
    }),
    retry: false,
    enabled,
    staleTime: 1000 * 15,
  });

  useEffect(() => {
    if (query.error) {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
  }, [query.error]);

  return query;
}
